import {
  PartyPopper,
  Sun,
  Briefcase,
  Gift,
  Stethoscope,
  Palmtree,
  Circle,
  User,
  Activity,
  Thermometer,
  Baby,
  Building,
  HeartHandshake,
  HardHat,
  Car,
  type LucideIcon,
} from "lucide-react";
import type { EventType } from "@/types";

export interface EventTypeMeta {
  value: EventType;
  label: string;
  color: string;
  icon: LucideIcon;
  /** Substring patterns (lowercased) that bias the auto-suggest toward this type. */
  keywords: string[];
  /**
   * If true, a title that starts with a real person name (e.g. "Wolfgang")
   * is enough to suggest this type. Used to bias suggestions toward personal
   * categories when the title looks like "Wolfgang X" or "Wolfgang - X".
   */
  personalDefault?: boolean;
}

export const EVENT_TYPES: EventTypeMeta[] = [
  {
    value: "urlaub",
    label: "Urlaub",
    color: "bg-cyan-100 text-cyan-800",
    icon: Palmtree,
    keywords: ["urlaub", "resturlaub", "kurzurlaub", "verlängertes wochenende", "verlängertes wocheende", "brückentag", "ferien"],
    personalDefault: true,
  },
  {
    value: "privat",
    label: "Privat",
    color: "bg-slate-100 text-slate-800",
    icon: User,
    keywords: ["privat", "privattermin", "frei", "verhindert", "nicht erreichbar", "nicht ereichbar", "weg bis", "putzfrau"],
    personalDefault: true,
  },
  {
    value: "arzttermin",
    label: "Arzttermin",
    color: "bg-red-100 text-red-800",
    icon: Stethoscope,
    keywords: [
      "arzt", "arzttermin", "artzttermin", "artzt", "hno", "orthopä", "mrt",
      "nuklear", "zahn", "krankenhaus", "ambulan", "klinik", "amtsarzt",
      "urologe", "neuro", "hausarzt", "finger", "bandscheibe", "lungen",
      "schlaf", "kinderchirurgie", "kinder reha", "augenarzt", "tierarzt",
      "blutuntersuchung", "osteopat", " op",
    ],
    personalDefault: true,
  },
  {
    value: "physio",
    label: "Physio / Therapie",
    color: "bg-orange-100 text-orange-800",
    icon: Activity,
    keywords: ["physio", "ems", "ergotherapie", "kg_therapie", "kg-therapie", "kg therapie", "therapie"],
    personalDefault: true,
  },
  {
    value: "krank",
    label: "Krank",
    color: "bg-red-200 text-red-900",
    icon: Thermometer,
    keywords: ["krank", "krankenstand"],
    personalDefault: true,
  },
  {
    value: "kinder_kindergarten_schule",
    label: "Kinder / Kindergarten / Schule",
    color: "bg-yellow-100 text-yellow-800",
    icon: Baby,
    keywords: ["kindergarten", "kinderarzt", "kita", "schule"],
    personalDefault: true,
  },
  {
    value: "betriebsurlaub_subunternehmer",
    label: "Betriebsurlaub Subunternehmer",
    color: "bg-stone-200 text-stone-900",
    icon: Building,
    keywords: ["betriebsurlaub", "betriebsferien", "betriebsuaub", "betrieburlaub"],
  },
  {
    value: "sonderurlaub",
    label: "Sonderurlaub",
    color: "bg-violet-100 text-violet-800",
    icon: HeartHandshake,
    keywords: ["sonderurlaub", "beerdigung", "hochzeit", "kirchl", "adoptionsanhörung", "familiengericht", "urnenbeisetzung", " reha", " kur"],
    personalDefault: true,
  },
  {
    value: "baustellentermin",
    label: "Baustellentermin",
    color: "bg-amber-100 text-amber-800",
    icon: HardHat,
    keywords: [
      "bv:", "baustelle", "ortstermin", "bauleiter", "schweißbahn",
      "zimmererarbeit", "rg-", "rg.", "dachdecker", "innenausbau",
      "hausübergabe", "abnahme",
    ],
  },
  {
    value: "privat_kfz",
    label: "Auto / Werkstatt",
    color: "bg-zinc-100 text-zinc-800",
    icon: Car,
    keywords: ["werkstatt", "reifenwechsel", "inspektion", "reifen", "volvo", "autoaufbereitung"],
    personalDefault: true,
  },
  {
    value: "company_event",
    label: "Firmenevent",
    color: "bg-blue-100 text-blue-800",
    icon: PartyPopper,
    keywords: ["weihnachtsfeier", "sommerfest", "jahresabschluss", "jahresempfang", "neujahrsempfang", "betriebsfest", "firmenevent", "firmenenvent"],
  },
  {
    value: "holiday",
    label: "Feiertag",
    color: "bg-green-100 text-green-800",
    icon: Sun,
    keywords: ["feiertag", "ostern", "karfreitag", "ostermontag", "pfingst", "aschermittwoch", "rosenmontag", "altweiberfasching", "faschingsdienstag", "tag der deutschen einheit"],
  },
  {
    value: "meeting",
    label: "Besprechung",
    color: "bg-purple-100 text-purple-800",
    icon: Briefcase,
    keywords: ["besprechung", "bauleiterbesprechung", "vertriebsmeeting", "plandurchsprache", "baumeeting", "schulung", "webinar", "seminar", "prüfung", " logo"],
  },
  {
    value: "birthday",
    label: "Geburtstag",
    color: "bg-pink-100 text-pink-800",
    icon: Gift,
    keywords: ["geburtstag"],
    personalDefault: true,
  },
  {
    value: "other",
    label: "Sonstiges",
    color: "bg-gray-100 text-gray-800",
    icon: Circle,
    keywords: [],
  },
];

const BY_VALUE = new Map<EventType, EventTypeMeta>(
  EVENT_TYPES.map((m) => [m.value, m])
);

/**
 * Resolve label / color / icon for a given event_type, with a safe fallback
 * to "other". Used by every page that renders an event badge so adding a
 * new type is a one-line change to EVENT_TYPES.
 */
export function getEventTypeConfig(value: string | null | undefined): EventTypeMeta {
  if (value && BY_VALUE.has(value as EventType)) {
    return BY_VALUE.get(value as EventType)!;
  }
  return BY_VALUE.get("other")!;
}

/**
 * Suggest the most likely event_type for a free-form title. Order matters:
 * company-level types first (so "GPS Weihnachtsfeier" → firmenevent, not
 * privat), then personal types. The first name is used to bias toward
 * personalDefault types when the title looks like "Wolfgang X".
 *
 * Mirrors the former api/recategorize_migrated_events.php rules (CLI since
 * removed) — these suggestions are now the single source of truth.
 */
export function suggestEventType(title: string, firstName?: string): EventType {
  const t = (title || "").toLowerCase().trim();
  if (!t) return "other";

  const startsWithFirstName =
    !!firstName && firstName.length > 1 && t.startsWith(firstName.toLowerCase());

  for (const meta of EVENT_TYPES) {
    if (meta.value === "other") continue;
    if (meta.keywords.some((k) => t.includes(k))) {
      return meta.value;
    }
  }

  // No keyword matched. If the title begins with a real person name and the
  // best guess is therefore "personal", fall back to the highest-priority
  // personalDefault type (urlaub → privat → arzttermin → physio).
  if (startsWithFirstName) {
    const personalPriority: EventType[] = [
      "urlaub", "privat", "arzttermin", "physio", "krank",
      "kinder_kindergarten_schule", "sonderurlaub",
    ];
    return personalPriority[0];
  }

  return "other";
}
